import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type HTMLAttributes,
  type ReactNode,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TABLE — REAL `<table>` SEMANTICS, COMPOSED. Follows the five rules in `Button.tsx`.
 * ═══════════════════════════════════════════════════════════════════════════
 * This console is a list of extensions, a list of trunks, a call log. Every one of them is a
 * data table, and a data table is the single component where the difference between "looks
 * right" and "is right" is largest — a grid of `<div>`s is pixel-identical and conveys nothing:
 * no row/column count, no "column 3 of 7", no way to hear the header when you land in a cell.
 *
 * ── 1. NATIVE ELEMENTS, AND NO ARIA ROLES ANYWHERE ─────────────────────────
 * `<table>/<thead>/<tbody>/<tr>/<th>/<td>` already carry `table`, `rowgroup`, `row`,
 * `columnheader`, `cell`. Re-declaring them with `role="table"` on a div is strictly worse: the
 * roles exist but the *implicit relationships* — which header names which cell — are computed by
 * the browser from the table's grid geometry, and a div tree has no geometry to compute from.
 *
 * ⚠️ THE COROLLARY THAT BITES: **never put `block`, `flex` or `grid` on any element in here.**
 * Changing a table element's `display` does not merely restyle it — Chrome and WebKit drop the
 * element's implicit role when its display type stops being the tabular one, so a `flex` on a
 * `<tr>` to line up an action button silently converts the whole table into a stack of generic
 * containers. Lay out INSIDE a `<td>`, never ON one. That is why the cell components take a
 * `className` for the cell and expect layout to live in their children.
 *
 * ── 2. THE ACCESSIBLE NAME IS A REQUIRED PROP, NOT AN OPTIONAL CHILD ───────
 * `caption` is required on `Table.Root`. A `<Table.Caption>` child would be the more symmetric
 * API and it would be optional by construction — TypeScript cannot demand that a particular
 * child be present — so the first table written in a hurry would ship unnamed, announced as
 * "table" with no indication of what it lists, and nothing would report it. As a prop the
 * compiler is the reviewer.
 *
 * It renders a real `<caption>` (hidden with `sr-only`, not `display:none`, so it stays in the
 * accessibility tree) and it does double duty: it names the table AND, by id, it names the
 * scroll region below.
 *
 * ── 3. THE SCROLL CONTAINER IS FOCUSABLE, AND THAT IS WCAG 2.1.1 ───────────
 * A wide table lives in `overflow-x-auto`. A pointer user drags it; a keyboard user cannot
 * reach the scrollbar at all, so the columns past the fold are simply unreachable — a Keyboard
 * (2.1.1) failure that no amount of correct table markup fixes. `tabIndex={0}` plus
 * `role="region"` and a name makes it a real stop that arrow keys then scroll.
 *
 * The tab stop is UNCONDITIONAL rather than applied only when the table actually overflows.
 * Detecting overflow means measuring, measuring means a ResizeObserver, and the observer is
 * wrong for at least the first frame — during which the focus order changes under a user who
 * is already tabbing. A predictable extra stop beats a focus order that moves.
 *
 * `region` and not `group`: a region is a landmark, so it also appears in the landmark rotor as
 * "Extensions region", which is how a screen-reader user jumps to a table without walking the
 * page. That is why the name matters twice.
 *
 * ── 4. SORTING IS `aria-sort` ON THE HEADER **PLUS** A REAL BUTTON ─────────
 * Two separate requirements that are easy to conflate. `aria-sort` on the `<th>` is how the
 * state is *exposed*; a `<button>` inside the `<th>` is how the sort is *operated*. A `<th>`
 * with an `onClick` and no button is invisible to the keyboard and has no role that says it can
 * be activated — the state would be announced perfectly and be unreachable.
 *
 * `aria-sort` is emitted only for the column that is actually sorted (`sort` left undefined
 * elsewhere). It is a per-table-singleton in ARIA: marking every sortable column
 * `aria-sort="none"` is legal but means each header is announced with a redundant "not sorted",
 * and `none` is the default value anyway. A caller that wants that noise can pass `'none'`
 * explicitly; it is not the default.
 */

export type TableDensity = 'comfortable' | 'compact';

/**
 * Density is context rather than a prop on every cell. `<Table.Root density="compact">` with
 * cells that each need reminding is the API where one forgotten cell has different padding and
 * the row looks subtly broken — and the fix is in a file the reader is not looking at.
 */
const TableDensityContext = createContext<TableDensity>('comfortable');

type TableSection = 'head' | 'body' | 'foot';

/**
 * Which rowgroup a cell finds itself in, used for ONE thing: the default `scope` of a
 * `Table.HeaderCell`. A `<th>` in `<thead>` names a column; a `<th>` in `<tbody>` is the row's
 * own label and names a row. Both are `<th>` and the difference is invisible at the call site,
 * so getting it from the surrounding section is the only version that is right by default.
 *
 * `scope` is not decoration: without it a screen reader guesses the association from the
 * table's shape, and it guesses wrong exactly on the tables that are complicated enough to need
 * the help.
 */
const TableSectionContext = createContext<TableSection>('body');

const CELL_PADDING: Record<TableDensity, string> = {
  comfortable: 'px-4 py-3',
  compact: 'px-3 py-1.5',
};

export interface TableRootProps extends TableHTMLAttributes<HTMLTableElement> {
  /**
   * The table's accessible name. Required — see the header. Say what the rows ARE ("SIP
   * extensions"), not that it is a table; "table" is already announced.
   *
   * `NonNullable<ReactNode>` and not plain `ReactNode`, because `ReactNode` INCLUDES `null` and
   * `undefined`. Omitting the prop is a compile error either way, but plain `ReactNode` also
   * accepts `caption={maybeTitle}` where `maybeTitle: string | undefined` — which is how the
   * name actually goes missing in practice (a value off a loading query, a lookup that missed).
   * That would compile, render `<caption>` empty, and leave the table and its scroll region
   * both unnamed, with nothing to report it. Narrowing the type is what makes the header's
   * "the compiler is the reviewer" claim true rather than nearly true.
   */
  caption: NonNullable<ReactNode>;
  /**
   * Show the caption above the table as a visible heading line. It is in the accessibility tree
   * either way; this only decides whether it is also on screen.
   */
  captionVisible?: boolean;
  density?: TableDensity;
  /** Class for the scrollable region. `className` goes to the `<table>` itself. */
  containerClassName?: string;
}

const TableRoot = forwardRef<HTMLTableElement, TableRootProps>(function TableRoot(
  { caption, captionVisible = false, density = 'comfortable', containerClassName, className, children, ...rest },
  ref,
) {
  const captionId = useId();

  return (
    <div
      /**
       * See header §3. The name comes from the caption by id rather than a duplicated
       * `aria-label`, so the region and the table cannot drift apart when the caption is
       * reworded — and there is no second string to translate.
       */
      role="region"
      aria-labelledby={captionId}
      tabIndex={0}
      className={cn(
        /**
         * `relative` makes the region the containing block of anything absolutely positioned
         * inside the table — and every visually hidden label is: `sr-only` IS absolute. Without
         * it, the "Actions" header that `ExtensionsPage` hides this way took the viewport as its
         * containing block, escaped the scroller it sits in, and widened the PAGE: 396px of
         * document on a 390px phone, a sideways wobble on any page whose table does the same.
         */
        'relative w-full overflow-x-auto rounded-lg border border-border bg-surface',
        containerClassName,
      )}
    >
      <table
        ref={ref}
        className={cn('w-full border-collapse text-left text-sm text-fg', className)}
        {...rest}
      >
        <caption
          id={captionId}
          className={cn(
            'text-left',
            // `sr-only` clips it; it is NOT `display:none`, which would take the table's
            // accessible name away with it. See styles.css for why the clip-rect form is the
            // only visually-hidden technique assistive technology still reads.
            captionVisible
              ? 'border-b border-border px-4 py-3 text-sm font-semibold text-fg'
              : 'sr-only',
          )}
        >
          {caption}
        </caption>
        <TableDensityContext.Provider value={density}>{children}</TableDensityContext.Provider>
      </table>
    </div>
  );
});

export type TableHeadProps = HTMLAttributes<HTMLTableSectionElement>;

const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(function TableHead(
  { className, children, ...rest },
  ref,
) {
  return (
    <TableSectionContext.Provider value="head">
      <thead
        className={cn('border-b border-border bg-surface-sunken text-fg-muted', className)}
        {...rest}
        ref={ref}
      >
        {children}
      </thead>
    </TableSectionContext.Provider>
  );
});

export type TableBodyProps = HTMLAttributes<HTMLTableSectionElement>;

const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(function TableBody(
  { className, children, ...rest },
  ref,
) {
  return (
    <TableSectionContext.Provider value="body">
      <tbody className={cn(className)} {...rest} ref={ref}>
        {children}
      </tbody>
    </TableSectionContext.Provider>
  );
});

export type TableFootProps = HTMLAttributes<HTMLTableSectionElement>;

const TableFoot = forwardRef<HTMLTableSectionElement, TableFootProps>(function TableFoot(
  { className, children, ...rest },
  ref,
) {
  return (
    <TableSectionContext.Provider value="foot">
      <tfoot
        className={cn('border-t border-border bg-surface-sunken font-medium text-fg', className)}
        {...rest}
        ref={ref}
      >
        {children}
      </tfoot>
    </TableSectionContext.Provider>
  );
});

export type TableRowProps = HTMLAttributes<HTMLTableRowElement>;

/**
 * No `selected` prop, and no hover highlight by default.
 *
 * `aria-selected` is not permitted on a `<tr>` inside a plain `<table>` — it belongs to `grid`
 * and `treegrid`, widgets with their own two-dimensional keyboard model that this component
 * deliberately is not. Selection here is a real checkbox in the first cell: it is focusable,
 * it has a name, it works with the keyboard, and its state is a checkbox state rather than an
 * attribute a screen reader will refuse to read.
 *
 * The hover tint is left to the caller for the same class of reason: a row that lights up under
 * the pointer reads as clickable, and on an inert row there is nothing to click. Add
 * `className="hover:bg-surface-sunken"` where the row genuinely leads somewhere.
 */
const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
  { className, ...rest },
  ref,
) {
  return <tr ref={ref} className={cn('border-b border-border last:border-0', className)} {...rest} />;
});

export type TableSortDirection = 'ascending' | 'descending' | 'none';

export interface TableHeaderCellProps
  extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'scope'> {
  /**
   * Defaults from the surrounding section: `col` inside `Table.Head`, `row` inside a body or
   * foot. Override for a spanning header (`colgroup`/`rowgroup`).
   */
  scope?: 'col' | 'row' | 'colgroup' | 'rowgroup';
  /**
   * The direction this column is CURRENTLY sorted in. Leave undefined on every column that is
   * not the sorted one — see header §4. Present without `onSort` describes a sort the server
   * applied and the user cannot change, which is a legitimate thing to announce.
   */
  sort?: TableSortDirection;
  /** Makes the header operable. Fires on click, Enter and Space, because it is a real button. */
  onSort?: () => void;
  /** Right-aligns and uses tabular figures, so digits line up column-wise. */
  numeric?: boolean;
}

/** Direction glyphs. `aria-hidden` at the call site — `aria-sort` is what carries the state. */
const SORT_GLYPH: Record<TableSortDirection, string> = {
  ascending: '↑',
  descending: '↓',
  none: '↕',
};

const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  function TableHeaderCell({ scope, sort, onSort, numeric = false, className, children, ...rest }, ref) {
    const density = useContext(TableDensityContext);
    const section = useContext(TableSectionContext);
    const resolvedScope = scope ?? (section === 'head' ? 'col' : 'row');

    return (
      <th
        ref={ref}
        scope={resolvedScope}
        aria-sort={sort}
        className={cn(
          'font-semibold whitespace-nowrap',
          numeric && 'text-right tabular-nums',
          CELL_PADDING[density],
          className,
        )}
        {...rest}
      >
        {onSort ? (
          <button
            type="button"
            onClick={onSort}
            /**
             * `min-h-6` is 24px — WCAG 2.5.8 Target Size (Minimum), the same floor `Button`
             * documents. A sort control is the one that most often ends up as bare 16px text
             * because it lives inside a cell that already has padding; the cell's padding is not
             * part of the target.
             *
             * No focus ring here: `:focus-visible` in styles.css owns that globally.
             */
            className={cn(
              'inline-flex min-h-6 items-center gap-1 rounded-sm px-1 -mx-1',
              'font-semibold text-fg-muted transition-colors duration-150 hover:text-fg',
            )}
          >
            {children}
            {/**
             * Hidden from the accessible name on purpose. The button's name stays exactly the
             * visible column label, which satisfies WCAG 2.5.3 (Label in Name) for a voice-
             * control user saying "click Extension" — appending "sorted ascending" to the name
             * would make the state readable twice and the name harder to speak, while
             * `aria-sort` on the `<th>` already reports it in the place screen readers look.
             */}
            <span aria-hidden="true" className="text-xs">
              {SORT_GLYPH[sort ?? 'none']}
            </span>
          </button>
        ) : (
          children
        )}
      </th>
    );
  },
);

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Right-aligns and uses tabular figures. Alignment is presentation only — see below. */
  numeric?: boolean;
}

/**
 * `numeric` right-aligns and switches on `tabular-nums`, and that is the whole of it. It does
 * NOT set `aria-*` anything: "this column holds numbers" is not a state, and the value in the
 * cell is already the value a screen reader reads. `tabular-nums` matters more than the
 * alignment — proportional digits make 1,111 narrower than 8,888, so a column of durations
 * cannot be compared by length the way a sighted user actually compares them.
 */
const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(function TableCell(
  { numeric = false, className, ...rest },
  ref,
) {
  const density = useContext(TableDensityContext);

  return (
    <td
      ref={ref}
      className={cn(
        'align-middle text-fg',
        numeric && 'text-right tabular-nums',
        CELL_PADDING[density],
        className,
      )}
      {...rest}
    />
  );
});

export {
  TableRoot,
  TableHead,
  TableBody,
  TableFoot,
  TableRow,
  TableHeaderCell,
  TableCell,
};

/**
 * The composed namespace. `<Table.Root caption="…"><Table.Head>…` reads as the markup it
 * produces, which for a table — where the nesting IS the semantics — is worth more than it is
 * for a flat component. The individually named exports above exist for the barrel and for
 * anyone who would rather import one part.
 */
export const Table = {
  Root: TableRoot,
  Head: TableHead,
  Body: TableBody,
  Foot: TableFoot,
  Row: TableRow,
  HeaderCell: TableHeaderCell,
  Cell: TableCell,
} as const;
